import { Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Input from '@/components/Inputs/Input';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useColor from '@/hooks/domain/useColor';
import useTimezoneSelect from '@/hooks/domain/useTimezoneSelect';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceLanguageSelect from '@/hooks/domain/useWorkspaceLanguageSelect';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useInputs } from '@/hooks/pure/useInputs';
import useCommonStore from '@/stores/pure/CommonStore';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import useUIStore from '@/stores/pure/UIStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import { Flex, Space } from '../../../components/base';
import SettingHeader from '../Header';
import { SettingSection } from '../SettingComponents';

const GeneralSettingBody = () => {
  const setState = useCommonStore((state) => state.setState);
  const setSidebarHidden = useUIStore((state) => state.setSidebarHidden);
  const { isAdmin } = useUserRole();
  const { navigate } = useWorkspaceRouter();
  const color = useColor();
  const {
    currWorkspace,
    fetchWorkspaceMembers,
    changeWorkspaceName,
    changeWorkspaceTimezone,
    deleteCurrWorkspace,
    updateAnonymization,
    updatePasswordProtection,
    updateLanguageSetting,
    leaveWorkspace,
  } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
    fetchWorkspaceMembers: state.fetchWorkspaceMembers,
    changeWorkspaceName: state.changeWorkspaceName,
    changeWorkspaceTimezone: state.changeWorkspaceTimezone,
    deleteCurrWorkspace: state.deleteCurrWorkspace,
    updateAnonymization: state.updateAnonymization,
    updatePasswordProtection: state.updatePasswordProtection,
    updateLanguageSetting: state.updateLanguageSetting,
    leaveWorkspace: state.leaveWorkspace,
  }));
  const [langOptions, selectedLang, setSelectedLang] =
    useWorkspaceLanguageSelect(currWorkspace?.language);
  const [timezoneOptions, selectedTimezone, setSelectedTimezone] =
    useTimezoneSelect(currWorkspace?.timezone);

  const [dataAnonymization, setDataAnonymization] = useState(
    !!currWorkspace?.data_anonymization,
  );

  const [passwordProtection, setPasswordProtection] = useState(
    !!currWorkspace?.password_protection,
  );

  const [state, onChange] = useInputs({
    workspaceName: currWorkspace ? currWorkspace.name : '',
  });

  const [changeNameError, setChangeNameError] = useState<string | null>(null);

  const isNameChange = useMemo(() => {
    return state.workspaceName && currWorkspace?.name !== state.workspaceName;
  }, [currWorkspace?.name, state.workspaceName]);

  const handleSaveChangeClick = async () => {
    const result = await changeWorkspaceName(state.workspaceName);
    if (result === true) {
      toast(
        `Workspace name is successfully changed to ${state.workspaceName}`,
        'success',
      );
      setChangeNameError(null);
    } else {
      const error = result as string;
      setChangeNameError(error);
    }
  };

  const isTimezoneChange: boolean = useMemo(() => {
    return (
      selectedTimezone !== NOT_ASSIGNED_LABEL &&
      selectedTimezone !== currWorkspace?.timezone
    );
  }, [currWorkspace?.timezone, selectedTimezone]);

  const handleTimezoneSaveChange = async () => {
    if (isTimezoneChange) {
      try {
        changeWorkspaceTimezone(selectedTimezone);
        toast(
          `Workspace timezone is successfully changed to ${selectedTimezone}`,
          'success',
        );
      } catch (e) {
        toast(
          `Workspace timezone is failed to change to ${selectedTimezone}`,
          'fail',
        );
        console.error(e);
      }
    }
  };

  const handleAnonymizationSave = async () => {
    await updateAnonymization(dataAnonymization);
    toast(
      `Data Anonymization ${dataAnonymization ? 'Activated' : 'Deactivated'}`,
      'success',
    );
  };

  const handlePasswordProtection = async () => {
    await updatePasswordProtection(passwordProtection);
    toast(
      `Password Protection ${
        passwordProtection ? 'Activated' : 'Deactivated'
      }`,
      'success',
    );
  };

  const handleDeleteClick = () => {
    popup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete the workspace',
      message: 'Are you sure you want to delete this workspace?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setState({ appLoadingState: true });
        setSidebarHidden(true);
        const result = await deleteCurrWorkspace();
        setSidebarHidden(false);
        if (result !== false) {
          const redirectUrl = result as string;
          navigate(redirectUrl);
          setTimeout(() => {
            setState({ appLoadingState: false });
          }, 1000);
        }
      },
    });
  };

  useEffect(() => {
    fetchWorkspaceMembers();
  }, [currWorkspace, fetchWorkspaceMembers]);

  const handleLangSaveChange = () => {
    if (!selectedLang) return toast('Language must be selected', 'fail');
    updateLanguageSetting(selectedLang);
    toast(`Workspace language setting is successfully changed`, 'success');
  };

  const handleLeaveClick = async () => {
    popup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Leave the workspace',
      message: 'Are you sure you want to leave this workspace?',
      confirmText: 'Leave',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setState({ appLoadingState: true });
        setSidebarHidden(true);
        const result = await leaveWorkspace();
        setSidebarHidden(false);
        if (result !== false) {
          const redirectUrl = result as string;
          navigate(redirectUrl);
          setTimeout(() => {
            setState({ appLoadingState: false });
          }, 1000);
        }
      },
    });
  };

  if (!currWorkspace) return <></>;

  return (
    <ScrollArea className="h-screen">
      <SettingHeader title="Workspace" />
      <Wrapper d="column">
        <SettingSection>
          <Flex d="column" j="flex-start" g="4px">
            <Typo type="body2" weight="sb" color={color.gray800}>
              Workspace name
            </Typo>
            <Typo type="body2" color={color.gray700}>
              Edit your workspace title here.
            </Typo>
          </Flex>
          <Flex d="column" g="4px">
            <Flex g="16px">
              <Flex style={{ width: 400 }}>
                <Input
                  name="workspaceName"
                  sizeType="l"
                  value={state.workspaceName}
                  onChange={onChange}
                  disabled={isAdmin ? false : true}
                />
              </Flex>
              <Button
                variant="outlined"
                sizeType="l"
                disabled={isAdmin && isNameChange ? false : true}
                onClick={handleSaveChangeClick}
              >
                Save change
              </Button>
            </Flex>
            {changeNameError && (
              <Flex j="flex-start">
                <Typo type="label2" style={{ color: color.red600 }}>
                  {changeNameError}
                </Typo>
              </Flex>
            )}
          </Flex>
        </SettingSection>
        <SettingSection>
          <Flex d="column" g="4px">
            <Typo type="body2" weight="sb" color={color.gray800}>
              Timezone
            </Typo>
            <Flex d="column">
              <Typo type="body2" color={color.gray700}>
                You can set a timezone for the workspace. Changing a timezone
                may change dates of the feedback, hence the potential change in
                weekly insights.
              </Typo>
              <Typo type="body2" color={color.gray600}>
                Default timezone is the local timezone of the admin when the
                user set up the workspace.
              </Typo>
            </Flex>
            <Space h="16px" />
            <Flex d="column" g="4px">
              <Flex g="16px">
                <Select
                  showSearch
                  style={{ width: 400 }}
                  options={timezoneOptions}
                  size="large"
                  defaultValue={currWorkspace.timezone}
                  onChange={(key) => setSelectedTimezone(key)}
                  disabled={!isAdmin}
                />

                <Button
                  variant="outlined"
                  sizeType="l"
                  disabled={!isAdmin || !isTimezoneChange}
                  onClick={handleTimezoneSaveChange}
                >
                  Save change
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </SettingSection>
        <SettingSection>
          <Flex d="column" j="flex-start" g="4px">
            <Typo type="label2" weight="sb" color={color.gray800}>
              Language Setting for AI
            </Typo>
            <Space h="8px" />
            <Typo type="body2" color={color.gray600}>
              AI outputs to be generated in the language selected below.
            </Typo>
          </Flex>
          <Flex g="16px">
            <Select
              style={{ width: 400 }}
              size="large"
              value={selectedLang}
              onChange={(val) => setSelectedLang(val)}
              options={langOptions}
              disabled={!isAdmin}
            />

            <Button
              variant="outlined"
              sizeType="l"
              disabled={!isAdmin || selectedLang === currWorkspace?.language}
              onClick={handleLangSaveChange}
            >
              Save change
            </Button>
          </Flex>
        </SettingSection>

        <SettingSection>
          <Flex a="center" g="16px">
            <Typo type="label2" weight="sb" color={color.gray800}>
              Data anonymization
            </Typo>
            <ToggleRadio
              status={dataAnonymization}
              onChange={(s) => setDataAnonymization(s)}
              disabled={!isAdmin}
            />
          </Flex>
          <Typo type="body3" color={color.gray600} style={{ width: 600 }}>
            Syncly uses data masking techniques: replacing specific identifying
            information from the dataset with asterisks (****) to ensure privacy
            protection. This includes sensitive details such as names,
            addresses, phone numbers, and social security numbers.
          </Typo>
          <div>
            <Button
              variant="outlined"
              sizeType="l"
              disabled={!!currWorkspace.data_anonymization === dataAnonymization}
              onClick={handleAnonymizationSave}
            >
              Save change
            </Button>
          </div>
        </SettingSection>

        <SettingSection>
          <Flex a="center" g="16px">
            <Typo type="label2" weight="sb" color={color.gray800}>
              Password Protection for CSV
            </Typo>
            <ToggleRadio
              status={passwordProtection}
              onChange={(s) => setPasswordProtection(s)}
              disabled={!isAdmin}
            />
          </Flex>
          <Typo type="body3" color={color.gray600} style={{ width: 600 }}>
            For extra layer of data security, you can make password protection
            mandatory to all CSV files downloads for this workspace.
          </Typo>
          <div>
            <Button
              variant="outlined"
              sizeType="l"
              disabled={!!currWorkspace.password_protection === passwordProtection}
              onClick={handlePasswordProtection}
            >
              Save change
            </Button>
          </div>
        </SettingSection>

        <SettingSection>
          <Flex d="column" g="4px">
            <Typo type="body2" weight="sb" color={color.gray800}>
              Leave workspace
            </Typo>
            <Typo type="body2" color={color.gray700}>
              This will leave the workspace and you will lose access to this
              workspace.
            </Typo>
          </Flex>
          <Button
            variant="error_outlined"
            sizeType="l"
            disabled={isAdmin ? true : false}
            onClick={handleLeaveClick}
            className="w-50"
          >
            Leave workspace
          </Button>
        </SettingSection>

        <SettingSection>
          <Flex d="column" g="4px">
            <Typo type="body2" weight="sb" color={color.gray800}>
              Delete workspace
            </Typo>
            <Typo type="body2" color={color.gray700}>
              This will permanently delete this workspace and all the data
              within. You will also lose access to this workspace.
            </Typo>
          </Flex>
          <Button
            variant="error"
            sizeType="l"
            disabled={isAdmin ? false : true}
            onClick={handleDeleteClick}
            className="w-50"
          >
            Delete workspace
          </Button>
        </SettingSection>
      </Wrapper>
    </ScrollArea>
  );
};

const Wrapper = styled(Flex)`
  width: 100%;
  padding: 0 32px;
  height: 100%;
`;

export default GeneralSettingBody;
